import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Icon } from "@/lib/ui";
import { Loader2 } from "lucide-react";
import Pager from "@/components/Pager";

const LIMIT = 25;

export default function AdminUsers() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [skip, setSkip] = useState(0);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/users?skip=${skip}&limit=${LIMIT}${search ? `&search=${search}` : ""}`);
    setData(data.data);
  }, [search, skip]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSkip(0); }, [search]);

  const toggle = async (u) => {
    const status = u.status === "blocked" ? "active" : "blocked";
    try { await api.post(`/admin/users/${u.id}/status`, { status }); toast.success(`User ${status}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-display font-black text-3xl tracking-tight">Users</h1>
      <div className="relative max-w-sm">
        <Icon name="Search" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="user-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / email" className="w-full h-11 pl-9 pr-4 rounded-full border border-input focus:outline-none focus:ring-2 focus:ring-ring" />
      </div>
      <div className="rounded-2xl border border-border bg-background overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-muted-foreground"><tr>
            {["Name", "Email", "Role", "Tags", "Status", ""].map((h) => <th key={h} className="text-left font-semibold px-4 py-3 whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {data === null ? <tr><td colSpan={6} className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></td></tr> :
              data.items.map((u) => (
                <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-semibold whitespace-nowrap">{u.name}</td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{u.email}</td>
                  <td className="px-4 py-3"><span className="text-xs font-bold px-2 py-1 rounded-full bg-secondary">{u.role}</span></td>
                  <td className="px-4 py-3">{u.tag_count}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-bold px-2 py-1 rounded-full ${u.status === "blocked" ? "bg-[#FFECEA] text-status-lost" : "bg-[#EAF9EF] text-status-found"}`}>{u.status || "active"}</span></td>
                  <td className="px-4 py-3 text-right">{u.role !== "admin" && <button data-testid={`toggle-user-${u.email}`} onClick={() => toggle(u)} className="tap font-semibold text-status-active">{u.status === "blocked" ? "Unblock" : "Block"}</button>}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {data && <Pager skip={skip} limit={LIMIT} total={data.total} onPage={setSkip} />}
    </div>
  );
}
